import { FEATURE_FLAGS } from '@mondaygpt/constants'
import { selectCurrentUserId } from 'modules/common'
import { useAppSelector } from 'modules/core'
import { selectIsLoggedIn } from 'modules/common/selectors/authentication-view.selector'
import { useFeatureGateState } from 'modules/feature-flags'
import { useCallback, useEffect, useSyncExternalStore } from 'react'
import { errorReportingService } from 'services/error-reporting/error-reporting.service'
import { getReferralInfo, type ReferralInfoDto } from './referrals.service'

/** PostHog flag that master-switches the whole referral program on the client. */
export const REFERRAL_GATE = FEATURE_FLAGS.referralProgram

/**
 * Module-level share of the `/v1/referrals/me` fetch. The sidebar renders on
 * every authenticated view and the page/prompts mount alongside it, so without
 * this each surface would fire its own identical GET. One in-flight/settled
 * promise is reused across all consumers for a short TTL; failures are evicted
 * immediately so the next mount retries.
 */
const REFERRAL_INFO_TTL_MS = 30_000
let referralInfoCache: { userId: string; ts: number; promise: Promise<ReferralInfoDto> } | null =
  null

const getReferralInfoShared = (userId: string): Promise<ReferralInfoDto> => {
  const now = Date.now()
  if (
    referralInfoCache &&
    referralInfoCache.userId === userId &&
    now - referralInfoCache.ts < REFERRAL_INFO_TTL_MS
  ) {
    return referralInfoCache.promise
  }
  const promise = getReferralInfo()
  const entry = { userId, ts: now, promise }
  referralInfoCache = entry
  promise.catch(() => {
    if (referralInfoCache === entry) referralInfoCache = null
  })
  return promise
}

/**
 * Shared, module-level snapshot of the referral info so EVERY consumer (sidebar
 * dot, waiting card, celebration mount, claim hook) reflects the same data and a
 * refetch/Pusher-driven refresh from any one of them updates all of them on the
 * same tick. Without this, each `useReferralProgram` instance kept its own
 * `info` state and a claim's refetch would only clear `claimable` for the
 * caller — leaving a stale sidebar dot and waiting card behind.
 *
 * `userId` scopes the snapshot: a consumer only trusts it when it matches the
 * currently-authenticated user (guards a switch/logout race). Read via
 * `useSyncExternalStore` so it stays a stable reference between notifications.
 */
interface ReferralSnapshot {
  userId: string | null
  info: ReferralInfoDto | null
  /** True once a fetch (or the decision not to fetch) has settled for `userId`. */
  resolved: boolean
}

let snapshot: ReferralSnapshot = { userId: null, info: null, resolved: false }
const listeners = new Set<() => void>()

const setSnapshot = (next: ReferralSnapshot): void => {
  snapshot = next
  listeners.forEach(listener => listener())
}

const subscribe = (listener: () => void): (() => void) => {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

const getSnapshot = (): ReferralSnapshot => snapshot

/**
 * Load `/me` into the shared snapshot. Deduped across consumers by
 * `getReferralInfoShared`; `force` busts the 30s cache so a claim's refetch hits
 * the network. Applies the result only if it's still for the requested user
 * (drops a late resolve after a user switch). Errors are non-fatal — the
 * snapshot resolves with `info: null` so surfaces just stay hidden — and are
 * re-thrown so a caller (refetch) can surface its own state if it wants to.
 */
const loadReferralInfo = async (userId: string, force = false): Promise<void> => {
  if (force) referralInfoCache = null
  try {
    const info = await getReferralInfoShared(userId)
    if (snapshot.userId === userId || force) {
      setSnapshot({ userId, info, resolved: true })
    }
    return
  } catch (error) {
    if (snapshot.userId === userId || force) {
      setSnapshot({ userId, info: null, resolved: true })
    }
    throw error
  }
}

interface ReferralProgramState {
  /**
   * True only when the `referral_program` gate is ON *and* the server reports
   * the program enabled for this user. Every referral surface (settings nav,
   * post-publish share, credits-exhausted share) renders nothing when false —
   * no dead menu entries, no half-loaded UI.
   */
  enabled: boolean
  /** The referral info from `GET /v1/referrals/me`, or null before it loads. */
  info: ReferralInfoDto | null
  /** True while the initial fetch is in flight (gate on, logged in). */
  loading: boolean
  /**
   * Force-refresh `info` from the server, bypassing the 30s module cache.
   * Because the snapshot is shared, this updates the claim UI, sidebar dot, and
   * stats across every consumer at once — used after a claim and by the
   * celebration component when Pusher signals a new approval. No-op when the
   * gate is off / logged out (nothing to refresh). Stable across renders.
   */
  refetch: () => Promise<void>
}

/**
 * Single source of truth for whether referral UI should show and what data to
 * render. Combines the reactive PostHog flag (mirrors `useEngagementGate` —
 * re-evaluates once the logged-in identity's flags land) with the
 * authenticated `/v1/referrals/me` fetch, held in a shared snapshot so all
 * consumers stay in lockstep.
 *
 * The fetch only fires when the gate is on and the user is logged in, so
 * disabled cohorts never hit the endpoint. If either the gate is off or the
 * server returns `enabled: false`, `enabled` is false and callers render null.
 */
export function useReferralProgram(): ReferralProgramState {
  const { enabled: gateEnabled, settled } = useFeatureGateState(REFERRAL_GATE)
  const isLoggedIn = useAppSelector(selectIsLoggedIn)
  const currentUserId = useAppSelector(selectCurrentUserId)

  const shared = useSyncExternalStore(subscribe, getSnapshot)

  const shouldFetch = gateEnabled && settled && isLoggedIn

  useEffect(() => {
    if (!shouldFetch) {
      // Settled but not fetching (gate off / logged out) = a real "off" answer.
      setSnapshot({ userId: currentUserId ?? null, info: null, resolved: settled })
      return
    }

    // Mark loading for this user before the fetch resolves (unless we already
    // hold data for them — avoids a flash back to loading on remount).
    if (snapshot.userId !== currentUserId) {
      setSnapshot({ userId: currentUserId ?? null, info: null, resolved: false })
    }

    loadReferralInfo(currentUserId ?? '').catch(error => {
      // Non-fatal: the program surfaces just stay hidden. Report so a broken
      // endpoint is visible without breaking the app.
      console.error('Failed to load referral info:', error)
      errorReportingService.captureException(error)
    })
  }, [shouldFetch, settled, currentUserId])

  const refetch = useCallback(async (): Promise<void> => {
    if (!shouldFetch) return
    await loadReferralInfo(currentUserId ?? '', true).catch(error => {
      // Same non-fatal handling as the initial fetch — the caller (celebration
      // component, ReferralPage) surfaces its own error state via toast.
      console.error('Failed to refetch referral info:', error)
      errorReportingService.captureException(error)
    })
  }, [shouldFetch, currentUserId])

  // Only trust the shared snapshot when it's for the current user (guards a
  // switch/logout race where a late resolve for the old user is still parked).
  const forCurrentUser = shared.userId === (currentUserId ?? null)
  const info = forCurrentUser ? shared.info : null
  const resolved = forCurrentUser ? shared.resolved : false

  // Loading until the user's flags have settled AND, when we do fetch, the
  // fetch has resolved. Derived (not state) so it's correct on the first render.
  const loading = !settled || (shouldFetch && !resolved)
  const enabled = shouldFetch && info?.enabled === true

  return { enabled, info, loading, refetch }
}
